import { AxiosResponse } from 'axios';
import { createSlice, PayloadAction, ActionCreatorWithoutPayload } from '@reduxjs/toolkit';

import { uploadClipThumbnail as uploadClipThumbServ } from 'services/file.service';
import { getClips, SetClipsRes, setClipThumbnailsForFinalCut, setHighlightThumbnail } from 'services/clip.service';
import { createSafeAsyncThunk } from 'store/store.helper';
import { dispatchMonitor } from 'components/app/editor/_mediator/monitor/monitor.slice';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { FcClip } from 'components/app/finalcuts/_mediator/fc-data.slice';

const SLICE_ID = 'clipThumbs';

export enum ThumbnailStatus {
  init,
  pending,
  uploaded,
  readyToUse,
  rejected,
}

export const INIT_THUMBNAIL = { status: ThumbnailStatus.init, url: null };
export type ThumbnailData = { status: ThumbnailStatus; url?: Url | null };
export type Thumbnails = Record<ClipId, ThumbnailData>;

export interface UploadClipThumbnail {
  storyId: StoryId;
  projectId: ProjectId;
  finalCutId?: FinalcutId;
  clipData: Clip | FcClip;
  blob: Blob;
}

export interface State {
  clipThumbnails: Thumbnails;
}

const initialState: State = {
  clipThumbnails: {},
};

// H E L P E R

export const handleClipThumbnails = (res: AxiosResponse<SetClipsRes>) => {
  if (!res.data.data) return {};
  const thumbs: Thumbnails = {};
  Object.entries(res.data.data).forEach(([clipId, url]) => {
    thumbs[clipId] = { status: ThumbnailStatus.readyToUse, url };
  });
  return thumbs;
};

export const selectThumbnailData = (clipId: ClipId, thumbnails: Thumbnails): ThumbnailData => {
  return thumbnails[clipId] ?? INIT_THUMBNAIL;
};

export const selectFcThumbnailData = (clipData: FcClip): ThumbnailData => {
  const url = clipData.thumbnailUrl;
  return url ? { status: ThumbnailStatus.readyToUse, url } : INIT_THUMBNAIL;
};

// T H U N K S

export const updThumbUrls = createSafeAsyncThunk(SLICE_ID + '/updThumbUrls', async (storyId: StoryId) => {
  const response = await getClips({ storyId });
  const thumbnails: Thumbnails = {};
  Object.entries(response.data).forEach(([clipId, data]) => {
    const thumbnailUrl = data.thumbnailUrl;
    thumbnails[clipId] = {
      status: thumbnailUrl ? ThumbnailStatus.readyToUse : ThumbnailStatus.init,
      url: thumbnailUrl,
    };
  });
  return thumbnails;
});

export const uploadClipThumbnail = createSafeAsyncThunk(
  SLICE_ID + '/uploadClipThumbnail',
  async (params: UploadClipThumbnail, { dispatch }) => {
    const { projectId, blob, clipData, finalCutId, storyId } = params;
    const clipId = clipData.clipId;
    const highlightId =
      clipData.isAutoHighlight && !clipData.isPinned && clipData.highlightId ? clipData.highlightId : undefined;
    const thumbnailUrl = await uploadClipThumbServ({ projectId, storyId, key: clipId, highlightId }, blob);

    if (finalCutId) {
      const res = await setClipThumbnailsForFinalCut({ finalCutId, data: { [clipId]: { thumbnailUrl } } });
      dispatch(updateClipThumbnails(handleClipThumbnails(res)));
      return { clipId, url: thumbnailUrl };
    }

    // Check if it's an unpinned highlight
    if (clipData.isAutoHighlight && !clipData.isPinned && clipData.highlightId) {
      // Call the new endpoint for unpinned highlights
      await setHighlightThumbnail({ storyId, data: { highlightId: clipData.highlightId, thumbnailUrl } });
      return { clipId, url: thumbnailUrl };
    }

    // Regular clip flow - dispatch monitor
    dispatch(dispatchMonitor({ thumbnail: clipId }) as unknown as ActionCreatorWithoutPayload);
    return { clipId, url: thumbnailUrl };
  }
);

// S L I C E

export const clipThumbSlice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setClipThumbnails: (state, action: PayloadAction<Thumbnails>) => {
      state.clipThumbnails = action.payload;
    },
    updateClipThumbnails: (state, action: PayloadAction<Thumbnails>) => {
      Object.entries(action.payload).forEach(([clipId, data]) => {
        state.clipThumbnails[clipId] = { ...state.clipThumbnails[clipId], ...data };
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadClipThumbnail.pending, (state, action) => {
      state.clipThumbnails[action.meta.arg.clipData.clipId] = { status: ThumbnailStatus.pending };
    });
    builder.addCase(uploadClipThumbnail.rejected, (state, action) => {
      state.clipThumbnails[action.meta.arg.clipData.clipId] = { status: ThumbnailStatus.rejected };
    });
    builder.addCase(uploadClipThumbnail.fulfilled, (state, action) => {
      state.clipThumbnails[action.payload.clipId] = { status: ThumbnailStatus.uploaded, url: action.payload.url };
    });
    builder.addCase(updThumbUrls.fulfilled, (state, action) => {
      state.clipThumbnails = action.payload;
    });
  },
});

export const { setClipThumbnails, updateClipThumbnails } = clipThumbSlice.actions;

export default clipThumbSlice.reducer;
